/**
 * The built-in door / window symbols as library entries (the fixed Doors /
 * Windows sections of the object library) and their editable specs for
 * "duplicate to edit": the exact data twin where there is one
 * (DATA_BUILTIN_DOOR_SPECS / _WINDOW_SPECS), else a snapshot of the symbol
 * at the default size that scales as a whole with the width.
 */

import {
  DATA_BUILTIN_DOOR_SPECS,
  DATA_BUILTIN_WINDOW_SPECS,
  DoorType,
  OpeningKind,
  OBJECT_DEFINITION_SCHEMA_VERSION,
  STANDARD_WINDOW_TYPE,
  createEmptyOpeningSpec,
  type ObjectDefinitionSpec,
  type ObjectOpeningKind,
  type ObjectShape,
  type Vec2,
} from 'shared-types';
import { t } from '../../../translation/markers';
import { buildOpeningGlyph } from '../../../utils/skeleton/openingGlyphs';
import { localOpeningFrame } from '../../../utils/skeleton/openingWallMap';
import { segmentsToPolylines } from '../fromBuiltin';

export interface BuiltinOpeningEntry {
  /** Library id (SCHEMA-free: only used on the library page). */
  id: string;
  kind: ObjectOpeningKind;
  /** The door type / window type value this symbol is stored as. */
  glyphType: string;
  /** Translation key. */
  label: string;
}

export const BUILTIN_OPENING_ENTRIES: readonly BuiltinOpeningEntry[] = [
  { id: 'DOOR_SINGLE', kind: 'DOOR', glyphType: DoorType.SINGLE, label: t('single') },
  { id: 'DOOR_DOUBLE', kind: 'DOOR', glyphType: DoorType.DOUBLE, label: t('double') },
  { id: 'DOOR_BIFOLD', kind: 'DOOR', glyphType: DoorType.BIFOLD, label: t('bifold') },
  { id: 'DOOR_SLIDING', kind: 'DOOR', glyphType: DoorType.SLIDING, label: t('sliding') },
  { id: 'DOOR_BYPASS_SLIDING', kind: 'DOOR', glyphType: DoorType.BYPASS_SLIDING, label: t('bypass sliding') },
  { id: 'DOOR_POCKET', kind: 'DOOR', glyphType: DoorType.POCKET, label: t('pocket') },
  { id: 'DOOR_SURFACE_SLIDING', kind: 'DOOR', glyphType: DoorType.SURFACE_SLIDING, label: t('surface sliding') },
  { id: 'DOOR_CLOSET', kind: 'DOOR', glyphType: DoorType.CLOSET, label: t('closet') },
  { id: 'DOOR_OPEN_DOORWAY', kind: 'DOOR', glyphType: DoorType.OPEN_DOORWAY, label: t('open doorway') },
  { id: 'WINDOW_STANDARD', kind: 'WINDOW', glyphType: STANDARD_WINDOW_TYPE, label: t('standard') },
];

/** Library section headers of the two fixed categories (translation keys). */
export const OPENING_CATEGORY_LABELS: Readonly<Record<ObjectOpeningKind, string>> = { DOOR: t('Doors'), WINDOW: t('Windows') };

const round = (v: number): number => Math.round(v * 1e5) / 1e5;

/** The symbol at `width` on the default wall, local frame (library cards, snapshots). */
export function builtinOpeningGlyph(entry: BuiltinOpeningEntry, width: number): number[] {
  const kind = entry.kind === 'DOOR' ? OpeningKind.DOOR : OpeningKind.WINDOW;
  return buildOpeningGlyph(
    {
      frame: localOpeningFrame(width / 2),
      kind,
      wallThickness: 0.2,
      hingeAtStart: true,
      swingPositive: true,
      doorType: kind === OpeningKind.DOOR ? (Object.values(DoorType).find((d) => d === entry.glyphType) ?? DoorType.SINGLE) : DoorType.SINGLE,
      rollable: false,
    },
    0,
  ).segments;
}

/** Editable spec of a built-in door / window symbol. */
export function builtinOpeningToSpec(entry: BuiltinOpeningEntry): ObjectDefinitionSpec {
  const twin = (entry.kind === 'DOOR' ? DATA_BUILTIN_DOOR_SPECS : DATA_BUILTIN_WINDOW_SPECS)[entry.glyphType];
  if (twin) return twin;
  const base = createEmptyOpeningSpec(entry.kind);
  const shapes: ObjectShape[] = segmentsToPolylines(builtinOpeningGlyph(entry, base.defaultSize.width)).map((points, i) => ({
    id: `s${i + 1}`,
    kind: 'polyline',
    points: points.map(([u, n]): Vec2 => [round(u), round(n)]),
    closed: false,
  }));
  return { ...base, schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION, shapes };
}
